import React, { useEffect, useState } from 'react';
import { Flame, Zap, Sparkles, TrendingUp } from 'lucide-react';
import api from '../lib/api';

const EVENT_ICONS = {
  app_opened: '📱',
  quiz_completed: '🧠',
  tutor_session_completed: '🎓',
  habit_completed: '✅',
  goal_checkin: '🎯',
  audiobook_listened: '🎧',
  buddy_reaction_sent: '💪',
};

const EVENT_LABELS = {
  app_opened: 'Showed up',
  quiz_completed: 'Quiz question',
  tutor_session_completed: 'Tutor session',
  habit_completed: 'Habit done',
  goal_checkin: 'Goal check-in',
  audiobook_listened: 'Chapter listened',
  buddy_reaction_sent: 'Buddy high-five',
};

export default function EffortCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setError(null);
    try {
      const { data } = await api.get('/rewards/summary');
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load effort score');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      // Award app_opened once per day (idempotent server-side)
      try { await api.post('/rewards/app-open'); } catch {}
      if (mounted) await load();
    })();
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <section className="bg-white rounded-3xl p-6 shadow-squishy border-2 border-cream text-center text-navy/60" data-testid="effort-card-loading">
        Loading effort score…
      </section>
    );
  }
  if (error) {
    return (
      <section className="bg-white rounded-3xl p-4 shadow-squishy border-2 border-red-200 text-center" data-testid="effort-card-error">
        <div className="text-navy/70 text-sm mb-2">{error}</div>
        <button onClick={() => { setLoading(true); load(); }} className="h-9 px-4 rounded-full bg-navy text-white font-heading font-bold text-xs" data-testid="effort-retry">Retry</button>
      </section>
    );
  }
  if (!data) return null;

  const streak = data.current_streak || 0;
  const streakColor = streak >= 7 ? 'from-orange-500 to-red-500' : streak >= 3 ? 'from-orange-400 to-orange-500' : 'from-slate-300 to-slate-400';

  return (
    <section className="rounded-3xl overflow-hidden shadow-squishyLg border-4 border-gold bg-white" data-testid="effort-card">
      <div className="bg-gradient-to-br from-navy via-teal to-navy p-5 text-white">
        <div className="flex items-center gap-2 mb-1">
          <TrendingUp className="w-4 h-4 text-gold" />
          <span className="text-gold font-heading font-bold text-[11px] uppercase tracking-wider">Effort Score</span>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div className="flex items-baseline gap-2">
              <span className="font-heading text-4xl font-bold" data-testid="effort-total">{data.effort_points_total}</span>
              <span className="text-white/70 text-sm">effort pts</span>
            </div>
            <div className="text-white/80 text-xs mt-0.5" data-testid="effort-today">
              +{data.effort_points_today} today · Effort &gt; outcome. Show up every day.
            </div>
          </div>
          <div className={`shrink-0 flex flex-col items-center px-3 py-2 rounded-2xl bg-gradient-to-br ${streakColor} shadow-lg`} data-testid="effort-streak-flame">
            <Flame className={`w-6 h-6 text-white ${streak > 0 ? 'animate-pulse' : 'opacity-70'}`} />
            <span className="font-heading text-2xl font-bold text-white leading-none">{streak}</span>
            <span className="text-[9px] font-heading font-bold text-white/90 uppercase tracking-wider">day{streak === 1 ? '' : 's'}</span>
          </div>
        </div>
      </div>
      {(data.breakdown || []).length > 0 && (
        <div className="p-4 grid grid-cols-2 gap-2 bg-cream" data-testid="effort-breakdown">
          {data.breakdown.slice(0, 6).map((b) => (
            <div key={b.event_type} className="flex items-center gap-2 bg-white rounded-2xl p-2 border-2 border-white" data-testid={`effort-breakdown-${b.event_type}`}>
              <span className="text-xl">{EVENT_ICONS[b.event_type] || '⭐'}</span>
              <div className="min-w-0 flex-1">
                <div className="font-heading font-bold text-navy text-xs leading-tight truncate">{EVENT_LABELS[b.event_type] || b.label}</div>
                <div className="text-teal text-[11px] font-bold">+{b.points} pts</div>
              </div>
            </div>
          ))}
        </div>
      )}
      {(data.breakdown || []).length === 0 && (
        <div className="p-4 bg-cream text-center text-navy/60 text-sm" data-testid="effort-empty">
          <Sparkles className="w-5 h-5 text-gold inline mr-1" />
          Answer a quiz, chat with the tutor, or check off a habit to earn your first effort points!
        </div>
      )}
    </section>
  );
}
